/**
 * Keys in the local storage that we set on a browser.
 *
 * These have to be CRAFTED because the data that's stored in them HAS NO EXPIRATION TIME. I.e. it's
 * important to not be too generic with words like "key" or "array", or get very specific with
 * applications like "sps-2023-foo" to make sure the same values aren't reused in 2024.
 */
const localStorageKeys = [
  'mwp-shopify-cart', // Items in the shopify cart for websites with ecommerce
  'mwp-snapkit-user-id', // SnapKit user id for SPS bitmoji reactions
  'mwp-snapkit-access-token', // Snapkit access token for SPS bitmoji reactions
  'sps-2023-firstName', // Stored first name of a person who's registered for SPS 2023
  'sps-2023-registrationStatus', // Registration status for someone who's registered for SPS 2023
  'sps-2023-interests', // Recorded interests for someone who's registeredf or SPS 2023
  'sps-2024-firstName', // Stored first name of a person who's registered for SPS 2024
  'sps-2024-registrationStatus', // Registration status for someone who's registered for SPS 2024
  'sps-2024-interests', // Recorded interests for someone who's registered for SPS 2024
  'experience-registration', // Registration data for experience site
  'mwp-form-data', // Form data for MWP forms
  'mwp-custom-motif', // When using custom debug mode to upload a motif from figma tokens
  'templates-note-dismissed', // Dismissed state for contentful-app templates note
] as const;

type LocalStorageKey = (typeof localStorageKeys)[number];

/**
 * Sets the local storage value for a given key. Type safe.
 *
 * Swallows browser errors (e.g. `QuotaExceededError` when storage is full,
 * `SecurityError` when storage access is denied) and logs a warning so callers
 * can treat this as fire-and-forget.
 */
export function setLocalStorageItem(key: LocalStorageKey, value: string): void {
  try {
    if (typeof localStorage === 'undefined') return;

    // eslint-disable-next-line no-restricted-syntax
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn(`Failed to set local storage item "${key}"`, error);
  }
}

/**
 * Retrieves the local storage value for a given key. Type safe.
 *
 * Returns `null` if storage is unavailable or the read throws (e.g. a
 * `SecurityError` in environments where storage access is blocked).
 */
export function getLocalStorageItem(key: LocalStorageKey): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;

    // eslint-disable-next-line no-restricted-syntax
    return localStorage.getItem(key) ?? null;
  } catch (error) {
    console.warn(`Failed to read local storage item "${key}"`, error);
    return null;
  }
}

/**
 * Removes an item from local storage for a given key. Type safe.
 *
 * Swallows browser errors and logs a warning so callers can treat this as
 * fire-and-forget.
 */
export function removeLocalStorageItem(key: LocalStorageKey): void {
  try {
    if (typeof localStorage === 'undefined') return;

    // eslint-disable-next-line no-restricted-syntax
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`Failed to remove local storage item "${key}"`, error);
  }
}
